import { HeroCard } from "@a2z/ui";
import { ButtonLink } from "@/shared/components/ButtonLink";

export type PageHeaderVariant = "plain" | "hero";

type PageHeaderProps = {
  children?: React.ReactNode;
  className?: string;
  hasBack?: boolean;
  /**
   * Layout variant. `"plain"` (default) preserves the current `.page-header`
   * markup — existing callers (Dashboard, Orders, Products, Settings, etc.)
   * render pixel-identically when this prop is omitted. `"hero"` renders a
   * `<HeroCard>` from `@a2z/ui` with the slots below.
   */
  variant?: PageHeaderVariant;
  /**
   * Hero-only: page title. When `variant="hero"`, `title` is required; if
   * omitted, `children` is used as the title fallback.
   */
  title?: React.ReactNode;
  /**
   * Hero-only: subtitle under the title. Ignored when `variant !== "hero"`.
   */
  subtitle?: React.ReactNode;
  /**
   * Hero-only: inline status pill rendered next to the title. Ignored when
   * `variant !== "hero"`.
   */
  statusSlot?: React.ReactNode;
  /**
   * Hero-only: primary CTA rendered at the inline-end side. Ignored when
   * `variant !== "hero"`.
   */
  actionSlot?: React.ReactNode;
};

export function PageHeader({
  children,
  className,
  hasBack,
  variant = "plain",
  title,
  subtitle,
  statusSlot,
  actionSlot,
}: PageHeaderProps) {
  if (variant === "hero") {
    return (
      <HeroCard
        title={title ?? children}
        subtitle={subtitle}
        statusSlot={statusSlot}
        actionSlot={actionSlot}
        className={className}
      />
    );
  }

  const classes = ["page-header", hasBack ? "page-header--with-back" : "", className]
    .filter(Boolean)
    .join(" ");
  return <header className={classes}>{children}</header>;
}

type PageActionsProps = {
  children?: React.ReactNode;
};

export function PageActions({ children }: PageActionsProps) {
  return <div className="page-actions">{children}</div>;
}

type PageTitleProps = {
  children: React.ReactNode;
};

export function PageTitle({ children }: PageTitleProps) {
  return (
    <h1 className="page-title text-2xl font-semibold text-[color:var(--text-primary)]">
      {children}
    </h1>
  );
}

type PageActionButtonProps = {
  href: string;
  label: string;
};

export function PageActionButton({ href, label }: PageActionButtonProps) {
  return (
    <ButtonLink
      href={href}
      variant="primary"
      size="lg"
      shape="pill"
    >
      {label}
    </ButtonLink>
  );
}
